import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import API_BASE_URL from '../config/api';
import { averageRatingFromReviews, clampRating0to5 } from '../utils/ratingStars';
import namkeFallback from '../assets/namke-fallback.png';

export const useRestaurantDetails = (restaurantId = null) => {
  const { id: urlId } = useParams();
  const id = restaurantId || urlId;
  const [restaurant, setRestaurant] = useState(null);
  const [dishes, setDishes] = useState([]);
  const [menus, setMenus] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [similarRestaurants, setSimilarRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  /** Fiche suspendue / indisponible (message + note admin optionnelle) */
  const [unavailableDetail, setUnavailableDetail] = useState(null);

  // Fonction pour récupérer les données du restaurant
  const fetchRestaurantData = async () => {
    try {
      setLoading(true);
      setError(null);
      setUnavailableDetail(null);
      console.log('Fetching data for restaurant ID:', id);

      // Détail restaurant avec profil (bannière, horaires) — endpoint public
      const restaurantResponse = await fetch(`${API_BASE_URL}/restaurants/public/${id}`);
      if (!restaurantResponse.ok) {
        let payload = {};
        try {
          payload = await restaurantResponse.json();
        } catch (_) {
          /* ignore */
        }
        const detail = payload?.detail;
        if (
          restaurantResponse.status === 404 &&
          detail &&
          typeof detail === 'object' &&
          detail.code === 'restaurant_unavailable'
        ) {
          setUnavailableDetail({
            message: detail.message || 'Ce restaurant n’est pas disponible.',
            suspensionNote: detail.suspension_note || null,
          });
          setRestaurant(null);
          setDishes([]);
          setMenus([]);
          setReviews([]);
          setSimilarRestaurants([]);
          return;
        }
        const msg =
          typeof detail === 'string'
            ? detail
            : detail?.message || 'Restaurant non trouvé';
        throw new Error(msg);
      }
      const restaurantData = await restaurantResponse.json();
      const profileData = restaurantData.profile || null;

      // Plats, menus, catégories, horaires structurés (en parallèle)
      const [dishesResponse, menusResponse, categoriesResponse, hoursResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/restaurants/${id}/dishes`),
        fetch(`${API_BASE_URL}/restaurants/${id}/menus-with-dishes`),
        fetch(`${API_BASE_URL}/categories/`),
        fetch(`${API_BASE_URL}/restaurants/${id}/hours`)
      ]);
      const dishesData = dishesResponse.ok ? await dishesResponse.json() : [];
      const menusData = menusResponse.ok ? await menusResponse.json() : [];
      const categoriesData = categoriesResponse.ok ? await categoriesResponse.json() : [];
      let hoursData = [];
      if (hoursResponse.ok) {
        try {
          const raw = await hoursResponse.json();
          hoursData = Array.isArray(raw) ? raw : [];
        } catch (_) {
          hoursData = [];
        }
      }

      // Récupérer les avis (si l'endpoint existe)
      let reviewsData = [];
      try {
        const reviewsResponse = await fetch(`${API_BASE_URL}/client-reviews/public/restaurants/${id}`);
        if (reviewsResponse.ok) {
          reviewsData = await reviewsResponse.json();
        }
      } catch (err) {
        console.warn('Avis non disponibles:', err);
      }

      const reviewsList = Array.isArray(reviewsData) ? reviewsData : [];
      const avgFromReviews = averageRatingFromReviews(reviewsList);
      const apiRatingRaw = restaurantData.rating != null ? Number(restaurantData.rating) : null;
      const apiRating = apiRatingRaw != null && !Number.isNaN(apiRatingRaw) ? clampRating0to5(apiRatingRaw) : null;
      const resolvedRating =
        avgFromReviews != null && (apiRating == null || apiRating === 0)
          ? avgFromReviews
          : apiRating != null
            ? apiRating
            : avgFromReviews ?? 0;

      // Construire l'objet restaurant complet à partir de l’API
      const restaurantComplete = {
        id: restaurantData.id,
        name: restaurantData.name,
        description: restaurantData.description,
        address: restaurantData.address,
        city: restaurantData.city || null,
        postal_code: restaurantData.postal_code || null,
        country: restaurantData.country || null,
        latitude: restaurantData.latitude ?? null,
        longitude: restaurantData.longitude ?? null,
        phone: restaurantData.phone,
        email: restaurantData.email,
        website: profileData?.website_url || null,
        cuisine_type: restaurantData.cuisine_type,
        price_range: restaurantData.price_range || (restaurantData.minimum_order != null ? (restaurantData.minimum_order >= 20 ? "$$$" : restaurantData.minimum_order >= 10 ? "$$" : "$") : "$$"),
        rating: resolvedRating,
        review_count: reviewsList.length,
        is_open: restaurantData.is_active !== false,
        is_favorite: false,
        banner_image: profileData?.banner_image_url || null,
        logo_image: profileData?.logo_url || null,
        slide_image_url: profileData?.slide_image_url || null,
        card_image_url: profileData?.card_image_url || null,
        restaurant_image_url: profileData?.restaurant_image_url || null,
        images_by_type: profileData?.images_by_type || {},
        opening_hours: profileData?.opening_hours || restaurantData.opening_hours || null,
        hours: hoursData,
        delivery_fee: restaurantData.delivery_fee != null ? Number(restaurantData.delivery_fee) : 0,
        minimum_order: restaurantData.minimum_order != null ? Number(restaurantData.minimum_order) : 0,
        delivery_time: restaurantData.delivery_time || "30-45 min",
        payment_methods: restaurantData.payment_methods || [],
        features: restaurantData.features || []
      };

      // Traiter les données des plats
      const processedDishes = Array.isArray(dishesData) ? dishesData : dishesData.dishes || [];
      
      // Menus déjà avec plats (menus-with-dishes)
      const processedMenus = Array.isArray(menusData) ? menusData : menusData.menus || menusData.value || [];

      // Traiter les catégories
      const processedCategories = Array.isArray(categoriesData) ? categoriesData : [];

      setRestaurant(restaurantComplete);
      setDishes(processedDishes);
      setMenus(processedMenus);
      setCategories(processedCategories);
      setReviews(reviewsList);

      // Restaurants similaires (optionnel)
      try {
        const similarResponse = await fetch(`${API_BASE_URL}/restaurants/${id}/similar?limit=6`);
        const similarData = similarResponse.ok ? await similarResponse.json() : [];
        setSimilarRestaurants(Array.isArray(similarData) ? similarData : []);
      } catch (e) {
        setSimilarRestaurants([]);
      }

    } catch (err) {
      console.error('Erreur lors du chargement des données du restaurant:', err);
      setError(err.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour récupérer les détails d'un plat
  const getDishDetails = async (dishId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/dishes/${dishId}`);
      if (response.ok) {
        return await response.json();
      }
    } catch (err) {
      console.error('Erreur lors du chargement des détails du plat:', err);
    }
    return null;
  };

  // Fonction pour récupérer les détails d'un menu
  const getMenuDetails = async (menuId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/menus/${menuId}`);
      if (response.ok) {
        return await response.json();
      }
    } catch (err) {
      console.error('Erreur lors du chargement des détails du menu:', err);
    }
    return null;
  };

  // Fonction pour filtrer les plats par catégorie
  const getDishesByCategory = (categoryId) => {
    if (categoryId === 'all') {
      return dishes;
    }
    return dishes.filter(dish => {
      if (typeof dish.category === 'object' && dish.category !== null) {
        return dish.category.id === categoryId;
      }
      return dish.category === categoryId;
    });
  };

  // Fonction pour obtenir le nom d'une catégorie
  const getCategoryName = (category) => {
    if (typeof category === 'object' && category !== null) {
      return category.name || 'Catégorie inconnue';
    }
    const categoryObj = categories.find(cat => cat.id === category);
    return categoryObj ? categoryObj.name : category || 'Catégorie inconnue';
  };

  // Fonction pour formater le prix
  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  // Fonction pour formater la note
  const formatRating = (rating) => {
    const r = Number(rating);
    if (Number.isNaN(r) || r <= 0) return '—';
    return r.toFixed(1);
  };

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Fonction pour obtenir l'URL de l'image
  const getImageUrl = (imageUrl, fallback = null) => {
    return imageUrl || fallback || namkeFallback;
  };

  // Fonction pour rafraîchir les données
  const refreshData = () => {
    fetchRestaurantData();
  };

  // Charger les données au montage du composant
  useEffect(() => {
    if (id) {
      fetchRestaurantData();
    }
  }, [id]);

  return {
    restaurant,
    dishes,
    menus,
    reviews,
    categories,
    similarRestaurants,
    loading,
    error,
    unavailableDetail,
    getDishDetails,
    getMenuDetails,
    getDishesByCategory,
    getCategoryName,
    formatPrice,
    formatRating,
    formatDate,
    getImageUrl,
    refreshData
  };
};